/**
 * The input the loader hands a system surface (layered shell surfaces, R-8, WS-6 §3.6). The shape
 * is WS-7's `SystemSurfaceInput` in `protocol.ts`; this module only fills it.
 */
import type { SurfaceServices } from '@zion/sdk-surfaces/v1';
import type {
  MaintenanceNotice,
  SessionPresence,
  SystemSurfaceInput,
  SystemSurfaceName,
} from '@zion/shell-system-surfaces/protocol';
import { logoUrlFor, NEUTRAL_PRODUCT_NAME } from '../platform/applyBranding.js';

/**
 * The brand row for a system page.
 *
 * `null` (no system mount yet) answers `{ resolved: false, logoUrl: null, productName: 'Console' }`.
 * With the system mount's services it reads `branding.current()` and `polarity.current()`:
 * `logoUrl` is `logoUrlFor(branding, resolved polarity, deployment)`; `productName` is the org's,
 * else the deployment's, else `NEUTRAL_PRODUCT_NAME`; `resolved` copies the branding state's. A
 * service that throws (a disposed mount) answers the `null` row. Pure read; never throws.
 */
export function systemBrand(services: SurfaceServices | null): SystemSurfaceInput['brand'] {
  const neutral = { resolved: false, logoUrl: null, productName: NEUTRAL_PRODUCT_NAME };
  if (services === null) return neutral;
  try {
    const { branding, deployment, resolved } = services.branding.current();
    const polarity = services.polarity.current().resolved;
    return {
      resolved,
      logoUrl: logoUrlFor(branding, polarity, deployment),
      productName: branding?.productName ?? deployment?.productName ?? NEUTRAL_PRODUCT_NAME,
    };
  } catch {
    return neutral;
  }
}

/**
 * Build the frozen input for `surface` from the document location, the brand row, the session
 * presence and the maintenance notice. Copies `pathname`, `search` and `hostname` from `loc`;
 * nested objects are copied and frozen. Pure; never throws.
 */
export function buildSystemInput(
  surface: SystemSurfaceName,
  loc: Pick<Location, 'pathname' | 'search' | 'hostname'>,
  brand: SystemSurfaceInput['brand'],
  session: SessionPresence,
  maintenance: MaintenanceNotice | null,
): SystemSurfaceInput {
  return Object.freeze({
    surface,
    location: Object.freeze({ pathname: loc.pathname, search: loc.search, hostname: loc.hostname }),
    brand: Object.freeze({ resolved: brand.resolved, logoUrl: brand.logoUrl, productName: brand.productName }),
    session,
    maintenance:
      maintenance === null
        ? null
        : Object.freeze({ retryAfterSeconds: maintenance.retryAfterSeconds, retrying: maintenance.retrying }),
  });
}
